/**
 * SIG-1063 — live office agent presence.
 *
 * Tile state is derived from real run/node snapshots and from the unified
 * `agents` channel (`agents.initial`, `agent.status`, `agent.thought`).
 * Nothing here invents activity: a running instance lights the current node;
 * a genuinely idle desk says why.
 */

import {
  AGENTS_CONFIG,
  FLOW_ORDER,
  nodeKeyToAgentKey,
  normalizeAgentKey,
  type AgentKey,
} from "../config/agents.config";
import { useFlowStore } from "../store/flow.store";
import type { AgentTileState, FlowNodeData } from "../types/flow.types";
import type { HeartbeatAliveness } from "../services/desk-heartbeat.service";
import { flowNodesEqual } from "./node-equality";

export type DeskStatus =
  | "no-run"
  | "live"
  | "completed"
  | "scanning"
  | "stale"
  | "closed";

export interface DeskScanClock {
  lastLookZ: string | null;
  nextLookZ: string | null;
}

export function resolveAgentKey(
  node: Pick<FlowNodeData, "id" | "agentKey">,
): AgentKey | null {
  if (node.agentKey) {
    return normalizeAgentKey(node.agentKey, node.id);
  }
  return nodeKeyToAgentKey(node.id);
}

export function nodeStatusToTileState(
  status: FlowNodeData["status"],
  _isCurrent: boolean,
): AgentTileState {
  switch (status) {
    case "running":
    case "waiting_hitl":
      return "running";
    case "success":
    case "skipped":
      return "done";
    case "filtered":
      // NO-TRADE verdict: the node completed and concluded "don't trade".
      // Rendering this as error was a lie — the agent did its job.
      return "no-trade";
    case "error":
      return "error";
    case "waiting":
      return "queued";
    case "idle":
    default:
      return "idle";
  }
}

export function deriveDeskStatus(input: {
  liveActive?: boolean;
  nodes: readonly FlowNodeData[];
  heartbeat?: { aliveness: HeartbeatAliveness } | null;
}): DeskStatus {
  if (
    input.liveActive ||
    input.nodes.some(
      (node) => node.status === "running" || node.status === "waiting_hitl",
    )
  ) {
    return "live";
  }
  const aliveness = input.heartbeat?.aliveness;
  if (aliveness === "closed") return "closed";
  if (aliveness === "live") return "scanning";
  if (aliveness === "stale") return "stale";
  if (input.nodes.length === 0) return "no-run";
  return "completed";
}

function scanningLine(scan?: DeskScanClock): string {
  const last = scan?.lastLookZ;
  const next = scan?.nextLookZ;
  if (last && next) return `Scanning — last look ${last} · next ${next}`;
  if (last) return `Scanning — last look ${last}`;
  if (next) return `Scanning — next ${next}`;
  return "Scanning";
}

function staleLine(scan?: DeskScanClock): string {
  const last = scan?.lastLookZ;
  if (last) return `Heartbeat stale — last look ${last}`;
  return "Heartbeat stale — last look unavailable";
}

export function honestTileLine(input: {
  state: AgentTileState;
  message?: string | null;
  deskStatus?: DeskStatus;
  predecessorName?: string | null;
  scan?: DeskScanClock;
}): string {
  const message = input.message?.trim();
  if (message) return message;

  if (input.deskStatus === "closed") {
    return input.scan?.nextLookZ
      ? `Market closed — next scan ${input.scan.nextLookZ}`
      : "Market closed — next scan unavailable";
  }

  switch (input.state) {
    case "running":
      return "Working this step.";
    case "done":
      return "Completed.";
    case "error":
      return "Failed.";
    case "no-trade":
      return "No entry — conditions not met.";
    case "queued":
      return input.predecessorName
        ? `Queued — waiting for ${input.predecessorName}.`
        : "Queued — waiting for earlier steps.";
    case "scanning":
      return scanningLine(input.scan);
    case "stale":
      return staleLine(input.scan);
    case "idle":
    default:
      if (input.deskStatus === "scanning") {
        return scanningLine(input.scan);
      }
      if (input.deskStatus === "stale") {
        return staleLine(input.scan);
      }
      if (input.deskStatus === "live") {
        return input.predecessorName
          ? `Waiting for ${input.predecessorName}.`
          : "Idle — waiting for this step.";
      }
      if (input.deskStatus === "completed") {
        return "Idle — last run already finished.";
      }
      return "Idle — no pipeline running.";
  }
}

export function buildAgentStateMap(
  currentNodes: FlowNodeData[],
  deskStatus?: DeskStatus,
): Map<AgentKey, { state: AgentTileState; node: FlowNodeData | null }> {
  const map = new Map<
    AgentKey,
    { state: AgentTileState; node: FlowNodeData | null }
  >();

  const defaultState: AgentTileState =
    deskStatus === "scanning"
      ? "scanning"
      : deskStatus === "stale"
        ? "stale"
      : currentNodes.length === 0
        ? "idle"
        : "queued";
  for (const key of FLOW_ORDER) {
    map.set(key, { state: defaultState, node: null });
  }

  if (currentNodes.length === 0) return map;

  const priority: Record<AgentTileState, number> = {
    running: 5,
    error: 4,
    "no-trade": 3,
    done: 2,
    scanning: 1,
    stale: 1,
    idle: 1,
    queued: 0,
  };

  for (const node of currentNodes) {
    const agentKey = resolveAgentKey(node);
    if (!agentKey) continue;

    const existing = map.get(agentKey);
    const tileState = nodeStatusToTileState(node.status, !!node.current);

    if (!existing || priority[tileState] > priority[existing.state]) {
      map.set(agentKey, { state: tileState, node });
    }
  }

  if (deskStatus === "scanning" || deskStatus === "stale") {
    const fallbackState: AgentTileState =
      deskStatus === "stale" ? "stale" : "scanning";
    for (const [key, entry] of map) {
      if (
        entry.state === "idle" ||
        entry.state === "queued" ||
        entry.state === "done"
      ) {
        map.set(key, { ...entry, state: fallbackState });
      }
    }
  }

  return map;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isActiveInstanceStatus(status: unknown): boolean {
  const normalized = String(status || "").toLowerCase();
  return (
    normalized === "running" ||
    normalized === "pending" ||
    normalized === "waiting_hitl" ||
    normalized === "paused"
  );
}

function agentKeyFromDisplayName(name: string): AgentKey | null {
  const normalized = name.trim().toLowerCase().replace(/_/g, "-");
  if (!normalized) return null;
  for (const meta of Object.values(AGENTS_CONFIG)) {
    if (
      meta.key === normalized ||
      meta.name.toLowerCase() === normalized ||
      meta.shortName.toLowerCase() === normalized
    ) {
      return meta.key;
    }
  }
  return null;
}

export function mergePresenceNode(
  nodes: FlowNodeData[],
  incoming: FlowNodeData,
): FlowNodeData[] {
  const key = resolveAgentKey(incoming);
  const next = nodes.slice();
  const idx = next.findIndex((node) => {
    const existing = resolveAgentKey(node);
    return (key !== null && existing === key) || node.id === incoming.id;
  });
  if (idx === -1) {
    next.push(incoming);
  } else {
    next[idx] = {
      ...next[idx],
      ...incoming,
      data: incoming.data ?? next[idx].data,
      message: incoming.message ?? next[idx].message,
    };
  }
  return flowNodesEqual(nodes, next) ? nodes : next;
}

function nodeStatusFromInstance(
  status: unknown,
): FlowNodeData["status"] {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "waiting_hitl" || normalized === "paused") {
    return "waiting_hitl";
  }
  if (normalized === "running" || normalized === "pending") {
    return "running";
  }
  if (
    normalized === "failed" ||
    normalized === "error" ||
    normalized === "cancelled"
  ) {
    return "error";
  }
  if (normalized === "rejected") {
    // NO-TRADE verdict, not a failure — see nodeStatusToTileState.
    return "filtered";
  }
  return "success";
}

function preferLiveInstance(
  instances: Record<string, unknown>[],
): Record<string, unknown> | null {
  if (instances.length === 0) return null;
  return (
    instances.find((instance) => {
      const runId = instance.run_id;
      return typeof runId === "string" && !runId.startsWith("runner-");
    }) ??
    instances[0] ??
    null
  );
}

function instanceMessage(instance: Record<string, unknown>): string | undefined {
  for (const key of ["message", "thought", "error_message"] as const) {
    const value = instance[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

function applyInstancePresence(instance: Record<string, unknown>): void {
  const store = useFlowStore.getState();
  if (!Array.isArray(store.currentNodes)) return;

  const currentNodeId =
    typeof instance.current_node === "string" ? instance.current_node : "";
  const agentKey =
    nodeKeyToAgentKey(currentNodeId) ?? normalizeAgentKey(null, currentNodeId);
  if (!agentKey || !currentNodeId) return;

  const incoming: FlowNodeData = {
    id: currentNodeId,
    name: AGENTS_CONFIG[agentKey].role,
    duration: 0,
    status: nodeStatusFromInstance(instance.status),
    agentKey,
    current: isActiveInstanceStatus(instance.status),
    message: instanceMessage(instance),
  };

  const merged = mergePresenceNode(store.currentNodes, incoming);
  const runId = typeof instance.run_id === "string" ? instance.run_id : null;
  if (merged === store.currentNodes && (!runId || store.liveRunId)) return;

  useFlowStore.setState((prev) => ({
    currentNodes: merged,
    liveRunId: prev.liveRunId || runId,
    currentRunInfo:
      prev.currentRunInfo ?? (runId ? { id: runId } : prev.currentRunInfo),
    liveActivityEpoch: prev.liveActivityEpoch + 1,
  }));
}

function applyThoughtPresence(payload: Record<string, unknown>): void {
  const store = useFlowStore.getState();
  if (!Array.isArray(store.currentNodes)) return;
  const runId = typeof payload.run_id === "string" ? payload.run_id.trim() : "";
  const allowed = new Set(
    [store.liveRunId, store.selectedRunId, store.currentRunInfo?.id].filter(
      (id): id is string => typeof id === "string" && id.length > 0,
    ),
  );
  const officeThought = runId.startsWith("office-");
  if (!runId || (!allowed.has(runId) && !officeThought)) return;

  const nodeId = String(payload.node_id || payload.node_name || "").trim();
  const agentName = String(payload.from_agent || payload.agent_name || "");
  const agentKey =
    (nodeId ? nodeKeyToAgentKey(nodeId) : null) ??
    normalizeAgentKey(null, nodeId || null) ??
    agentKeyFromDisplayName(agentName);
  if (!agentKey) return;

  const content = String(payload.content || payload.thought || "").trim();
  const live = !!store.liveActive || deriveDeskStatus({
    liveActive: store.liveActive,
    nodes: store.currentNodes ?? [],
  }) === "live";

  const incoming: FlowNodeData = {
    id: nodeId || agentKey,
    name: AGENTS_CONFIG[agentKey].role,
    duration: 0,
    status: live ? "running" : "success",
    agentKey,
    current: live,
    message: content || undefined,
  };

  const currentNodes = Array.isArray(store.currentNodes)
    ? store.currentNodes
    : [];
  const merged = mergePresenceNode(currentNodes, incoming);
  if (merged === currentNodes) return;

  useFlowStore.setState((prev) => ({
    currentNodes: merged,
    liveActivityEpoch: prev.liveActivityEpoch + 1,
  }));
}

/**
 * Apply an `agents` channel envelope to the office floor.
 * Safe no-op when the payload is not a live instance / owned thought.
 */
export function applyOfficeAgentPresence(data: unknown): void {
  if (!isRecord(data)) return;
  const type = String(data.type || "");
  const payload = data.payload ?? data.data;

  if (type === "agents.initial" && Array.isArray(payload)) {
    const instances = payload.filter(isRecord);
    // SIG-1133: apply every frame. Filtering to running-only dropped Wags/
    // Dollar terminal overlay frames so a refresh mid-fire only lit Devin.
    const toApply =
      instances.length > 0
        ? instances
        : (() => {
            const fallback = preferLiveInstance(instances);
            return fallback ? [fallback] : [];
          })();
    for (const instance of toApply) {
      applyInstancePresence(instance);
    }
    return;
  }

  if (
    (type === "agent.status" ||
      type === "agent.started" ||
      type === "agent.stopped") &&
    isRecord(payload)
  ) {
    applyInstancePresence(payload);
    return;
  }

  if (type === "agent.thought" && isRecord(payload)) {
    applyThoughtPresence(payload);
  }
}
